import { useEffect, useMemo } from 'react';

import {
	BoundActions,
	createHook,
	createStore,
	HookFunction,
	StoreActionApi,
} from 'react-sweet-state';

import { useRovoWorkspace } from '@townsquare/collab-context';
import { useOrgContextOrgId } from '@townsquare/org-context';
import { fg } from '@townsquare/stat-sig/gate';

import hrisClient from './client';
import { DisconnectConnectorError, SyncConnectorError } from './errors';
import { Connector, HRISFlowStep, State, TransitionTo } from './types';

const initialState: State = {
	availableConnectors: [],
	activeConnector: null,
	isLoading: false,
	error: null,
	isInitializing: false,
	isInitialized: false,
	currentStep: HRISFlowStep.CONNECT_HRIS,
	// TODO: Replace by TO_ORG_ADMIN_MANAGED when the enable_hris_disconnect_transition_to_admin-managed gate is cleaned up
	transitionTo: 'TO_MEMBER_INVITE',
};

const getCurrentStep = (
	availableConnectors: Connector[],
	activeConnector: Connector | null,
): HRISFlowStep => {
	if (activeConnector) {
		return HRISFlowStep.COMPLETED;
	}
	if (availableConnectors.length > 0) {
		return HRISFlowStep.SYNC_TEAMS;
	}
	return HRISFlowStep.CONNECT_HRIS;
};

type StoreApi = StoreActionApi<State>;

const actions = {
	resetStore:
		() =>
		({ setState }: StoreApi): void => {
			setState(initialState);
		},
	initHRISConnectorsStore:
		(orgId: string, siteId: string) =>
		async ({ setState, getState }: StoreApi): Promise<void> => {
			const { isInitializing, isInitialized } = getState();
			if (isInitializing || isInitialized) {
				return;
			}
			setState({ isInitializing: true, error: null });
			try {
				const [availableConnectors, activeConnector] = await Promise.all([
					hrisClient.getAvailableConnectors(orgId, siteId),
					hrisClient.getActiveConnector(orgId, siteId),
				]);
				setState({
					availableConnectors,
					activeConnector,
					isInitializing: false,
					isInitialized: true,
					error: null,
					currentStep: getCurrentStep(availableConnectors, activeConnector),
				});
			} catch (error) {
				setState({
					error: error as Error,
					isInitializing: false,
				});
			}
		},
	setActiveConnector:
		(orgId: string, siteId: string, connector: Connector) =>
		async ({ setState, getState }: StoreApi): Promise<void> => {
			setState({ isLoading: true, error: null });
			try {
				const activeConnector = await hrisClient.setActiveConnector(orgId, siteId, connector.id);
				const { availableConnectors } = getState();
				setState({
					activeConnector,
					isLoading: false,
					currentStep: getCurrentStep(availableConnectors, activeConnector),
				});
			} catch (error) {
				setState({
					error: new SyncConnectorError(
						error instanceof Error ? error.message : 'Unknown error',
						connector.type,
					),
					isLoading: false,
				});
			}
		},
	removeActiveConnector:
		(orgId: string, siteId: string, connector: Connector) =>
		async ({ setState, getState }: StoreApi): Promise<void> => {
			setState({ isLoading: true, error: null });
			const { transitionTo } = getState();
			// TODO: Remove this when the enable_hris_disconnect_transition_to_admin-managed gate is cleaned up
			// Temporary workaround to handle default store value for transitionTo as we don't have a way to FG at the file level.
			const transitionToGatedValue =
				transitionTo === 'TO_MEMBER_INVITE' &&
				fg('enable_hris_disconnect_transition_to_admin-managed')
					? 'TO_ORG_ADMIN_MANAGED'
					: transitionTo;
			try {
				await hrisClient.removeActiveConnector(orgId, siteId, connector.id, transitionToGatedValue);
				const { availableConnectors } = getState();
				setState({
					activeConnector: null,
					isLoading: false,
					currentStep: getCurrentStep(availableConnectors, null),
				});
			} catch (error) {
				setState({
					error: new DisconnectConnectorError(
						error instanceof Error ? error.message : 'Unknown error',
					),
					isLoading: false,
				});
			}
		},
	setTransitionTo:
		(transitionTo: TransitionTo) =>
		({ setState }: StoreApi): void => {
			setState({ transitionTo });
		},
};

const HRISConnectorsStore = createStore({
	initialState,
	actions,
});

export const useHRISConnectorsStore: HookFunction<
	{
		availableConnectors: Connector[];
		activeConnector: Connector | null;
		isLoading: boolean;
		error: Error | null;
		isInitializing: boolean;
		isInitialized: boolean;
		currentStep: HRISFlowStep;
		transitionTo: TransitionTo;
	},
	BoundActions<
		State,
		{
			resetStore: () => ({ setState }: StoreApi) => void;
			initHRISConnectorsStore: (
				orgId: string,
				siteId: string,
			) => ({ setState, getState }: StoreApi) => Promise<void>;
			setActiveConnector: (
				orgId: string,
				siteId: string,
				connector: Connector,
			) => ({ setState, getState }: StoreApi) => Promise<void>;
			removeActiveConnector: (
				orgId: string,
				siteId: string,
				connector: Connector,
			) => ({ setState, getState }: StoreApi) => Promise<void>;
			setTransitionTo: (transitionTo: TransitionTo) => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(HRISConnectorsStore, {
	selector: (state) => ({
		...state,
		availableConnectors: state.availableConnectors.filter(
			(c) => c.id !== state.activeConnector?.id,
		),
	}),
});

export function useHRISConnectors(): readonly [
	{
		availableConnectors: Connector[];
		activeConnector: Connector | null;
		isLoading: boolean;
		error: Error | null;
		isInitializing: boolean;
		isInitialized: boolean;
		currentStep: HRISFlowStep;
		transitionTo: TransitionTo;
	},
	{
		setActiveConnector: (connector: Connector) => Promise<void>;
		removeActiveConnector: (connector: Connector) => Promise<void>;
		setTransitionTo: (transitionTo: TransitionTo) => void;
	},
] {
	const [state, storeActions] = useHRISConnectorsStore();
	const orgId = useOrgContextOrgId();
	const siteId = useRovoWorkspace() ?? '';

	const actions = useMemo(
		() => ({
			setActiveConnector: (connector: Connector) =>
				storeActions.setActiveConnector(orgId, siteId, connector),
			removeActiveConnector: (connector: Connector) =>
				storeActions.removeActiveConnector(orgId, siteId, connector),
			setTransitionTo: (transitionTo: TransitionTo) => storeActions.setTransitionTo(transitionTo),
		}),
		[orgId, siteId, storeActions],
	);

	useEffect(() => {
		storeActions.initHRISConnectorsStore(orgId, siteId);
	}, [storeActions, orgId, siteId]);

	if (state.error && !state.isInitialized) {
		throw state.error;
	}

	return [state, actions] as const;
}
